/**
 * Tethys and Varro grammars: Ore Sling / Tethys, Crown Fork / Tethys, Proving Range / Varro, Colossus Slip / Varro.
 * Tethys: bundles of vertical foundry stacks with domed caps and heat collars; the held work dwarfs the body.
 * Varro: faceted slab bastions, wedge prows, ≤ 3 broad raked blade towers; no round bodies, rings or lattices.
 */
import { detailRng, gateQuadrant, lumClass, lumScale, oreClass, part, regionIndex, rf, rotYTo, segment } from './kit.ts'
import type { FamilyGrammar, MegaPart, Vec3 } from './types.ts'

const TAU = Math.PI * 2

/** A Tethys foundry-stack bundle round the y axis. Returns [bundle radius, top of the tallest stack]. */
function foundryBundle(out: MegaPart[], H: number, n: number, patternI: number, hoops: number, shift: number): readonly [number, number] {
  const sd = 0.2 * H
  const rb = n <= 3 ? sd * 0.62 : sd * (0.5 + 0.12 * n)
  let tallest = 0
  for (let i = 0; i < n; i++) {
    const t = n > 1 ? i / (n - 1) : 0
    let hf: number
    if (patternI === 0) hf = 1 - 0.45 * t
    else if (patternI === 1) hf = 0.6 + 0.4 * Math.sin(Math.PI * t)
    else if (patternI === 2) hf = i % 2 === 0 ? 1 : 0.62
    else hf = 0.55 + 0.45 * t
    const h = H * hf
    const b = (i / n) * TAU + shift
    const d = sd * (i % 2 === 0 ? 1 : 0.82)
    const x = Math.cos(b) * rb
    const z = Math.sin(b) * rb
    const step = i < n / 2 ? 1 : 2
    out.push(part('drum', [x, h / 2, z], [d, h, d], { step }))
    out.push(part('sphere', [x, h, z], [d, d * 0.55, d], { step }))
    out.push(part('drum', [x, h * 0.72, z], [d * 1.12, 0.02 * H, d * 1.12], { step, role: 'accent' }))
    tallest = Math.max(tallest, h)
  }
  for (let k = 0; k < hoops; k++) {
    const y = H * (0.12 + 0.18 * k)
    out.push(part('drum', [0, y, 0], [(rb + sd * 0.55) * 2, 0.045 * H, (rb + sd * 0.55) * 2], { step: 2 }))
  }
  return [rb + sd * 0.5, tallest]
}

// ── Ore Sling / Tethys ─────────────────────────────────────────────────────────────────────────────────────────────
// Round 2: one sweeping launcher. A heavy foundry-stack body on an armoured plinth; a continuous, dense helix of
// chamfered-square coil gates (gates overlap along a sealed rail) leaves the body low, sweeps outward and up 1–2 turns,
// and runs straight out of the last turn toward the gate bearing.
export const oreSlingTethys: FamilyGrammar = {
  kind: 'oreSling',
  maker: 'tethys',
  slots: [
    { name: 'stacks', options: 4 }, // 3/4/5/6 foundry stacks
    { name: 'skyline', options: 4 }, // descending, tall centre, two peaks, ramp
    { name: 'turns', options: 4 }, // 1, 1.25, 1.5, 2 helix turns
    { name: 'sweep', options: 4 }, // how far the helix swings out from the body: hugging, medium, wide, flaring
    { name: 'climb', options: 4 }, // low start ccw, low start cw, high start ccw, high start cw
    { name: 'coils', options: 3 }, // gate density: overlapping, touching, beaded
    { name: 'release', options: 4, site: gateQuadrant }, // the helix lets go toward the gate
    { name: 'hoops', options: 4, site: regionIndex }, // armoured hoops: gate one → rim four
  ],
  build(g, site) {
    const [stackI, skyI, turnI, radI, climbI, coilI, , hoopI] = g.slots
    const p = g.props
    const H = 1500 + 500 * p[0]
    const out: MegaPart[] = []
    const [rb, tallest] = foundryBundle(out, H, stackI + 3, skyI, hoopI + 1, p[1] * TAU)
    out.push(part('frustum', [0, 0.05 * H, 0], [rb * 2.7, 0.1 * H, rb * 2.7], { taper: 0.78 }))
    const turns = [1, 1.25, 1.5, 2][turnI]
    const ccw = climbI % 2 === 0
    const y0 = (climbI < 2 ? 0.15 : 0.38) * H
    const y1 = Math.max(tallest * 1.05, y0 + (0.95 + 0.35 * p[2]) * H)
    const gs0 = 0.24 * H
    const r0 = rb + gs0 * 0.55
    const r1 = r0 + [0.12, 0.3, 0.45, 0.6][radI] * H
    const gb = site.gateBearingRad
    const phiEnd = ccw ? gb - Math.PI / 2 : gb + Math.PI / 2
    const sgn = ccw ? 1 : -1
    const radiusAt = (t: number): number => r0 + (r1 - r0) * (radI === 3 ? t * t : Math.sqrt(t))
    const pathLen = turns * TAU * (r0 + r1) * 0.5 + (y1 - y0)
    const n = Math.max(24, Math.ceil(pathLen / (gs0 * [0.5, 0.68, 0.9][coilI])))
    const at = (t: number): Vec3 => {
      const phi = phiEnd - sgn * (1 - t) * turns * TAU
      const r = radiusAt(t)
      return [Math.cos(phi) * r, y0 + (y1 - y0) * t, Math.sin(phi) * r]
    }
    let prev = at(0)
    let tanEnd: Vec3 = [Math.cos(gb), 0, Math.sin(gb)]
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1)
      const q = at(t)
      const qn = at(Math.min(1, t + 0.5 / n))
      const qp = at(Math.max(0, t - 0.5 / n))
      const tan = norm([qn[0] - qp[0], qn[1] - qp[1], qn[2] - qp[2]])
      const gs = gs0 * (0.85 + 0.3 * t)
      const step = 3 + Math.min(1, Math.floor(2 * t))
      out.push(part('coilGate', q, [gs, gs * 0.3, gs], { rot: rotYTo(tan), step, role: i % 4 === 0 ? 'accent' : 'signature' }))
      // The sealed launch rail under the gates.
      if (i > 0) out.push(segment('drum', [prev[0], prev[1] - gs * 0.42, prev[2]], [q[0], q[1] - gs * 0.42, q[2]], 0.05 * H, 0.05 * H, { step, role: 'work' }))
      // Sealed pylons back to the body.
      if (i % 7 === 0) {
        const phi = Math.atan2(q[2], q[0])
        out.push(segment('drum', [Math.cos(phi) * rb * 0.8, q[1] * 0.85, Math.sin(phi) * rb * 0.8], [q[0], q[1] - gs * 0.45, q[2]], 0.06 * H, 0.06 * H, { step, role: 'work' }))
      }
      prev = q
      if (i === n - 1) tanEnd = tan
    }
    // Release (step 5): the gates run straight on out of the last turn, toward the gate bearing.
    const gsE = gs0 * 1.15
    for (let k = 1; k <= 5; k++) {
      const s = gsE * 0.5 * k
      const q: Vec3 = [prev[0] + tanEnd[0] * s, prev[1] + tanEnd[1] * s, prev[2] + tanEnd[2] * s]
      out.push(part('coilGate', q, [gsE * (1 - 0.05 * k), gsE * 0.3, gsE * (1 - 0.05 * k)], { rot: rotYTo(tanEnd), step: 5, role: 'signature' }))
    }
    const railEnd: Vec3 = [prev[0] + tanEnd[0] * gsE * 2.5, prev[1] + tanEnd[1] * gsE * 2.5 - gsE * 0.42, prev[2] + tanEnd[2] * gsE * 2.5]
    out.push(segment('drum', [prev[0], prev[1] - gsE * 0.42, prev[2]], railEnd, 0.05 * H, 0.05 * H, { step: 5, role: 'work' }))
    return out
  },
}

// ── Crown Fork / Tethys ────────────────────────────────────────────────────────────────────────────────────────────
export const crownForkTethys: FamilyGrammar = {
  kind: 'crownFork',
  maker: 'tethys',
  slots: [
    { name: 'tines', options: 3 }, // 2/3/4 tines
    { name: 'tineShape', options: 4 }, // straight, hooked, double-jointed, spatulate
    { name: 'stacks', options: 4 }, // 3/4/5/6 foundry stacks
    { name: 'rock', options: 4, site: oreClass }, // the ore signature shapes (and tints) the rock
    { name: 'rockSize', options: 3 },
    { name: 'grip', options: 4 }, // rock above, beside, body perched on it, canted
    { name: 'terraces', options: 3 }, // 3/5/7 carved terraces
    { name: 'floods', options: 4, site: lumClass }, // flood and radiator panels sized to the star
  ],
  build(g, site, stage) {
    const [tineI, shapeI, stackI, rockI, sizeI, gripI, terrI] = g.slots
    const p = g.props
    const H = 1500 + 500 * p[0]
    const out: MegaPart[] = []
    const R = [0.55, 0.7, 0.85][sizeI] * H * (0.9 + 0.2 * p[1])
    const bodyY = gripI === 2 ? R * 1.6 : 0
    const tmp: MegaPart[] = []
    const [rb, top] = foundryBundle(tmp, H * 0.8, stackI + 3, Math.floor(p[2] * 4), 2, p[3] * TAU)
    for (const q of tmp) out.push({ ...q, pos: [q.pos[0], q.pos[1] + bodyY, q.pos[2]] })
    // Rock centre and grip axis.
    let c: Vec3
    if (gripI === 0) c = [0, bodyY + top + R * 0.95, 0]
    else if (gripI === 1) c = [rb + R * 1.2, bodyY + top * 0.75, 0]
    else if (gripI === 2) c = [0, R * 0.85, 0]
    else c = [(top + R) * 0.5, bodyY + (top + R) * 0.85, 0]
    const A: Vec3 = gripI === 2 ? [0, bodyY, 0] : gripI === 1 ? [rb * 0.6, bodyY + top * 0.75, 0] : [0, bodyY + top * 0.85, 0]
    const ax: Vec3 = [c[0] - A[0], c[1] - A[1], c[2] - A[2]]
    const am = Math.hypot(ax[0], ax[1], ax[2])
    const a: Vec3 = [ax[0] / am, ax[1] / am, ax[2] / am]
    const e1: Vec3 = Math.abs(a[2]) < 0.9 ? norm(cross(a, [0, 0, 1])) : norm(cross(a, [1, 0, 0]))
    const e2: Vec3 = cross(a, e1)
    const tint = site.oreTint
    const tintOpt = tint === undefined ? {} : { tint }
    // The rock (step 2): its shape class comes from the ore.
    const rockOpts = { step: 2, role: 'work' as const, seed: g.detail, ...tintOpt }
    if (rockI === 0) out.push(part('rock', c, [R * 2, R * 1.8, R * 1.9], rockOpts))
    else if (rockI === 1) out.push(part('rock', c, [R * 2.8, R * 1.5, R * 1.7], { ...rockOpts, rot: [0, p[4] * TAU, 0.4] }))
    else if (rockI === 2) {
      out.push(part('rock', [c[0] - R * 0.45, c[1] - R * 0.2, c[2]], [R * 1.5, R * 1.6, R * 1.5], rockOpts))
      out.push(part('rock', [c[0] + R * 0.55, c[1] + R * 0.25, c[2]], [R * 1.2, R * 1.3, R * 1.2], { ...rockOpts, seed: g.detail ^ 0x55 }))
    } else {
      out.push(part('slab', c, [R * 1.7, R * 1.7, R * 1.5], { ...rockOpts, rot: [0.5, 0.3, 0.2], taper: 0.7 }))
      out.push(part('slab', c, [R * 1.5, R * 1.9, R * 1.4], { ...rockOpts, rot: [-0.3, 0.9, 0.7], taper: 0.6 }))
    }
    // Carved terraces (steps 3–5): ledges cut round the rock, more each stage.
    const nT = [3, 5, 7][terrI]
    const built = stage >= 5 ? nT : Math.floor((nT * Math.max(0, stage - 2)) / 3)
    for (let k = 0; k < nT; k++) {
      const s = -0.6 + (1.2 * (k + 0.5)) / nT
      const rr = R * Math.sqrt(Math.max(0.05, 1 - s * s)) * 1.02
      const q: Vec3 = [c[0] + a[0] * s * R, c[1] + a[1] * s * R, c[2] + a[2] * s * R]
      const step = k < built ? Math.min(5, 3 + Math.floor((3 * k) / nT)) : 5
      out.push(part('frustum', q, [rr * 2, R * 0.06, rr * 2], { rot: rotYTo(a), taper: 0.92, step, role: 'work', ...tintOpt }))
    }
    // Tines (step 1 roots, step 2 grips) hugging the rock.
    const nTi = [2, 3, 4][tineI]
    const w = 0.09 * H
    for (let i = 0; i < nTi; i++) {
      const psi = (i / nTi) * TAU + (nTi === 2 ? Math.PI / 2 : 0)
      const rdir: Vec3 = add(scale(e1, Math.cos(psi)), scale(e2, Math.sin(psi)))
      const P0 = add(A, scale(rdir, rb * 0.7))
      const P1 = add(add(c, scale(a, -R * 0.45)), scale(rdir, R * 1.15))
      const P2 = add(add(c, scale(a, R * 0.35)), scale(rdir, R * 1.08))
      const P3 = add(add(c, scale(a, R * 0.75)), scale(rdir, R * 0.55))
      out.push(segment('tine', P0, P1, w * 1.2, w, { taper: 0.8, step: 1, role: 'signature' }))
      if (shapeI === 0) out.push(segment('tine', P1, P2, w, w * 0.8, { taper: 0.4, step: 2, role: 'signature' }))
      else if (shapeI === 1) {
        out.push(segment('tine', P1, P2, w, w * 0.8, { taper: 0.8, step: 2, role: 'signature' }))
        out.push(segment('tine', P2, P3, w * 0.8, w * 0.7, { taper: 0.3, step: 2, role: 'signature' }))
      } else if (shapeI === 2) {
        const K = add(add(c, scale(a, -R * 0.1)), scale(rdir, R * 1.45))
        out.push(segment('tine', P1, K, w, w * 0.8, { taper: 0.9, step: 2, role: 'signature' }))
        out.push(segment('tine', K, P3, w * 0.9, w * 0.7, { taper: 0.35, step: 2, role: 'signature' }))
      } else {
        out.push(segment('tine', P1, P2, w, w * 0.8, { taper: 0.9, step: 2, role: 'signature' }))
        out.push(segment('plate', P2, add(P2, scale(a, R * 0.3)), w * 3, w * 0.5, { step: 2, role: 'signature' }))
      }
    }
    // Paired flood and radiator panels (step 5), sized to the star.
    const fs = lumScale(site) * 0.2 * H
    for (const s of [-1, 1]) {
      out.push(part('plate', [s * (rb + fs * 0.55), bodyY + top * 0.5, 0], [fs, fs * 0.7, 14], { step: 5, role: 'accent' }))
    }
    return out
  },
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
}
function norm(a: Vec3): Vec3 {
  const m = Math.hypot(a[0], a[1], a[2]) || 1
  return [a[0] / m, a[1] / m, a[2] / m]
}
function add(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
}
function scale(a: Vec3, s: number): Vec3 {
  return [a[0] * s, a[1] * s, a[2] * s]
}

/** A Varro faceted slab bastion facing bearing `b`, its centre `back` metres behind the origin. Returns its top and half-length. */
function bastion(out: MegaPart[], H: number, formI: number, b: number, hf: number, back: number): readonly [number, number] {
  const L = 1.25 * H
  const h = 0.36 * H * hf
  const rot: Vec3 = [0, -b, 0]
  const d: Vec3 = [Math.cos(b), 0, Math.sin(b)]
  const o: Vec3 = [-d[0] * back, 0, -d[2] * back]
  const at = (u: number, y: number): Vec3 => [o[0] + d[0] * u, y, o[2] + d[2] * u]
  if (formI === 0) {
    out.push(part('slab', at(0, h / 2), [L, h, L * 0.55], { rot, taper: 0.78 }))
    out.push(part('slab', at(L * 0.52, h * 0.4), [L * 0.42, h * 0.8, L * 0.42], { rot: [0, -b + Math.PI / 4, 0], taper: 0.5 }))
    out.push(part('slab', at(-L * 0.1, h * 1.15), [L * 0.55, h * 0.3, L * 0.35], { rot, taper: 0.7 }))
  } else if (formI === 1) {
    for (let k = 0; k < 3; k++) out.push(part('slab', at(-L * 0.05 * k, h * (0.2 + 0.4 * k)), [L * (1 - 0.22 * k), h * 0.4, L * 0.6 * (1 - 0.22 * k)], { rot, taper: 0.85 }))
  } else if (formI === 2) {
    for (const s of [-1, 1]) {
      const q = at(0, h * 0.5)
      out.push(part('slab', [q[0] - Math.sin(b) * s * L * 0.2, q[1], q[2] + Math.cos(b) * s * L * 0.2], [L, h, L * 0.3], { rot, taper: 0.7, shear: 0.15 }))
    }
    out.push(part('slab', at(-L * 0.1, h * 0.3), [L * 0.8, h * 0.6, L * 0.25], { rot, taper: 0.8 }))
    out.push(part('slab', at(L * 0.5, h * 0.35), [L * 0.36, h * 0.7, L * 0.36], { rot: [0, -b + Math.PI / 4, 0], taper: 0.45 }))
    out.push(part('slab', at(-L * 0.5, h * 0.3), [L * 0.3, h * 0.6, L * 0.3], { rot: [0, -b + Math.PI / 4, 0], taper: 0.5 }))
  } else {
    out.push(part('slab', at(0, h / 2), [L * 1.15, h, L * 0.4], { rot, taper: 0.7 }))
    out.push(part('slab', at(0, h / 2), [L * 0.7, h * 0.9, L * 0.7], { rot: [0, -b + Math.PI / 4, 0], taper: 0.6 }))
  }
  return [formI === 1 || formI === 0 ? h * 1.2 : h, L / 2]
}

/** A raked Varro blade tower. */
function bladeTower(out: MegaPart[], x: number, z: number, y0: number, h: number, w: number, b: number, rake: number, step: number): void {
  out.push(part('blade', [x, y0 + h / 2, z], [w, h, w * 0.4], { rot: [0, -b, 0], taper: 0.3, shear: -rake, step, role: 'signature' }))
}

// ── Proving Range / Varro ──────────────────────────────────────────────────────────────────────────────────────────
// Round 2: a bigger bastion set back from the lane, raked blade towers that dominate, and a lane with presence:
// a firing apron, then hulks and drone gates hundreds of metres across marching out along the gate line.
export const provingRangeVarro: FamilyGrammar = {
  kind: 'provingRange',
  maker: 'varro_security',
  slots: [
    { name: 'towers', options: 4 }, // pair, three, pair + prow tower, one great + two small
    { name: 'bastion', options: 4 }, // wedge prow, ziggurat, twin slab, long hex
    { name: 'rake', options: 3 },
    { name: 'laneLen', options: 4 },
    { name: 'targets', options: 3 }, // hulks, drone gates, mixed
    { name: 'laneLevel', options: 3 }, // low, mid, from the crown
    { name: 'aim', options: 4, site: gateQuadrant }, // the lane fires along the gate line
    { name: 'mass', options: 4, site: regionIndex }, // gate tall and new → rim squat and old
  ],
  build(g, site) {
    const [towI, basI, rakeI, lenI, tgtI, lvlI, , massI] = g.slots
    const p = g.props
    const H = 1700 + 500 * p[0]
    const out: MegaPart[] = []
    const b = site.gateBearingRad
    const d: Vec3 = [Math.cos(b), 0, Math.sin(b)]
    const side: Vec3 = [-Math.sin(b), 0, Math.cos(b)]
    const hf = [1.2, 1.05, 0.92, 0.8][massI]
    const back = 0.45 * H
    const [top, half] = bastion(out, H, basI, b, hf, back)
    const rake = [0.12, 0.25, 0.4][rakeI]
    const th = 1.05 * H * hf
    const towers: readonly (readonly [number, number, number])[] =
      towI === 0 ? [[-0.2, -0.24, 1], [-0.2, 0.24, 0.85]] : towI === 1 ? [[-0.28, -0.27, 0.8], [-0.1, 0, 1], [-0.28, 0.27, 0.8]] : towI === 2 ? [[-0.3, -0.22, 1], [-0.3, 0.22, 0.9], [0.3, 0, 0.55]] : [[-0.15, 0, 1.15], [-0.4, -0.3, 0.55], [-0.4, 0.3, 0.55]]
    towers.forEach(([u, v, s], i) => {
      const x = d[0] * (u * H - back) + side[0] * v * H
      const z = d[2] * (u * H - back) + side[2] * v * H
      bladeTower(out, x, z, top * 0.85, th * s, 0.32 * H * s, b, rake, i === 0 ? 2 : 3)
    })
    // The firing apron (step 3): a raked wedge running out of the bastion's front along the lane.
    const ly = [0.14 * H, top * 0.65, top + 0.08 * H][lvlI]
    const x0 = half - back
    const laneL = [1.0, 1.15, 1.3, 1.45][lenI] * H
    const apron = 0.36 * laneL
    out.push(part('blade', [d[0] * (x0 + apron / 2), Math.max(0.05 * H, ly - 0.08 * H), d[2] * (x0 + apron / 2)], [apron, 0.1 * H, 0.2 * H], { rot: [0, -b, 0], taper: 0.4, step: 3, role: 'work' }))
    // The live-fire lane (steps 4–5): hulks and drone gates at real size.
    const n = 6
    const r = detailRng(g.detail, 41)
    for (let k = 0; k < n; k++) {
      const t = x0 + apron * 0.8 + ((k + 0.5) / n) * (laneL - apron * 0.8)
      const q: Vec3 = [d[0] * t, ly + rf(r, -0.03, 0.03) * H, d[2] * t]
      const step = k < n / 2 ? 4 : 5
      const gate = tgtI === 1 || (tgtI === 2 && k % 2 === 1)
      if (gate) {
        const gw = 0.3 * H * (1 - 0.08 * k)
        for (const s of [-1, 1]) {
          out.push(part('blade', [q[0] + side[0] * s * gw * 0.5, q[1], q[2] + side[2] * s * gw * 0.5], [0.05 * H, gw, 0.05 * H], { rot: [0, -b, 0], taper: 0.5, step, role: 'work' }))
        }
        out.push(part('slab', [q[0], q[1] + gw * 0.5, q[2]], [0.06 * H, 0.04 * H, gw * 1.1], { rot: [0, -b, 0], step, role: 'work' }))
        out.push(part('slab', [q[0], q[1] - gw * 0.5, q[2]], [0.06 * H, 0.04 * H, gw * 0.9], { rot: [0, -b, 0], step, role: 'work' }))
      } else {
        const hs = rf(r, 0.2, 0.28) * H
        const hr: Vec3 = [0, -b + rf(r, -0.6, 0.6), rf(r, -0.35, 0.35)]
        out.push(part('slab', q, [hs * 1.6, hs * 0.45, hs * 0.55], { rot: hr, taper: 0.45, shear: 0.25, step, role: 'work' }))
        out.push(part('blade', [q[0], q[1] + hs * 0.3, q[2]], [hs * 0.5, hs * 0.4, hs * 0.08], { rot: hr, taper: 0.3, shear: -0.4, step, role: 'work' }))
      }
    }
    return out
  },
}

// ── Colossus Slip / Varro ──────────────────────────────────────────────────────────────────────────────────────────
// Round 2: the trough walls are lower and crested with raked blade fins, and the warship rides high in the V: a deep
// hull with a dorsal wedge and a raked bridge blade standing clear above the walls, so the side view reads as a ship.
export const colossusSlipVarro: FamilyGrammar = {
  kind: 'colossusSlip',
  maker: 'varro_security',
  slots: [
    { name: 'vee', options: 3 }, // trough opening angle
    { name: 'girder', options: 4 }, // fin crest, stepped, forked tips, buttressed
    { name: 'hull', options: 4 }, // wedge, dagger, hammerhead, split prow
    { name: 'hullLen', options: 3 },
    { name: 'base', options: 4 }, // bastion, two pylons, floating, stern bastion
    { name: 'sternTowers', options: 3 }, // 1/2/3 raked stern blades
    { name: 'aim', options: 4, site: gateQuadrant }, // the keel points at the gate it will launch through
    { name: 'buttresses', options: 4, site: regionIndex },
  ],
  build(g, site, stage) {
    const [veeI, girI, hullI, lenI, baseI, sternI, , butI] = g.slots
    const p = g.props
    const H = 1500 + 450 * p[0]
    const out: MegaPart[] = []
    const b = site.gateBearingRad
    const d: Vec3 = [Math.cos(b), 0, Math.sin(b)]
    const pp: Vec3 = [Math.sin(b), 0, -Math.cos(b)]
    const V = [0.45, 0.6, 0.75][veeI]
    const Lk = [1.6, 1.85, 2.1][lenI] * H
    const keelY = baseI === 2 ? 0.22 * H : 0.3 * H
    // Base (step 1).
    if (baseI === 0) out.push(part('slab', [0, keelY * 0.45, 0], [Lk * 0.8, keelY * 0.9, 0.35 * H], { rot: [0, -b, 0], taper: 0.7 }))
    else if (baseI === 1) {
      for (const s of [-0.3, 0.3]) out.push(part('slab', [d[0] * s * Lk, keelY * 0.45, d[2] * s * Lk], [0.3 * H, keelY * 0.9, 0.3 * H], { rot: [0, -b, 0], taper: 0.6 }))
    } else if (baseI === 3) {
      out.push(part('slab', [-d[0] * Lk * 0.45, keelY * 0.6, -d[2] * Lk * 0.45], [0.5 * H, keelY * 1.2, 0.5 * H], { rot: [0, -b, 0], taper: 0.65 }))
    }
    // The V girders (step 1): two raked blade walls along the keel, lower than the hull.
    const gh = 0.52 * H
    const crestY = keelY + gh * Math.cos(V)
    for (const s of [-1, 1]) {
      const c: Vec3 = [pp[0] * s * (gh / 2) * Math.sin(V), keelY + (gh / 2) * Math.cos(V), pp[2] * s * (gh / 2) * Math.sin(V)]
      const rot: Vec3 = [0, Math.PI / 2 - b, -s * V]
      out.push(part('blade', c, [0.07 * H, gh, Lk * 1.05], { rot, taper: girI === 1 ? 0.6 : 0.85, step: 1 }))
      const cx = pp[0] * s * gh * Math.sin(V)
      const cz = pp[2] * s * gh * Math.sin(V)
      if (girI === 0 || girI === 1) {
        // A crest of raked blade fins along the wall top, raking aft (step 2).
        const nf = girI === 0 ? 5 : 3
        for (let k = 0; k < nf; k++) {
          const e = -0.42 + (0.84 * k) / (nf - 1)
          const fl = (girI === 0 ? 0.3 : 0.45) * H
          const fh = (girI === 0 ? 0.2 : 0.16) * H * (1 - 0.25 * Math.abs(e))
          out.push(part('blade', [cx + d[0] * e * Lk, crestY + fh / 2, cz + d[2] * e * Lk], [fl, fh, 0.05 * H], { rot: [0, -b, 0], taper: 0.25, shear: -0.6, step: 2 }))
        }
      } else if (girI === 2) {
        for (const e of [-1, 1]) {
          const tip: Vec3 = [cx + d[0] * e * Lk * 0.47, crestY + 0.1 * H, cz + d[2] * e * Lk * 0.47]
          out.push(part('blade', tip, [0.3 * H, 0.3 * H, 0.06 * H], { rot: [0, -b, 0], taper: 0.2, shear: e * 0.5, step: 2 }))
        }
      } else {
        for (const e of [-0.35, 0, 0.35]) {
          const q: Vec3 = [pp[0] * s * gh * 0.75 + d[0] * e * Lk, keelY + gh * 0.25, pp[2] * s * gh * 0.75 + d[2] * e * Lk]
          out.push(part('blade', q, [0.06 * H, gh * 0.55, 0.12 * H], { rot: [0, Math.PI / 2 - b, s * 0.5], taper: 0.4, step: 2 }))
        }
      }
      for (let k = 0; k < butI; k++) {
        const e = -0.4 + (0.8 * (k + 0.5)) / Math.max(1, butI)
        const q: Vec3 = [pp[0] * s * gh * 0.62 + d[0] * e * Lk, keelY * 0.6, pp[2] * s * gh * 0.62 + d[2] * e * Lk]
        out.push(part('slab', q, [0.12 * H, keelY * 1.2, 0.08 * H], { rot: [0, -b, 0], taper: 0.4, step: 2 }))
      }
    }
    // Stern towers (step 2).
    for (let k = 0; k < sternI + 1; k++) {
      const off = (k - sternI / 2) * 0.22 * H
      const x = -d[0] * Lk * 0.52 + pp[0] * off
      const z = -d[2] * Lk * 0.52 + pp[2] * off
      bladeTower(out, x, z, keelY, (0.95 - 0.15 * Math.abs(k - sternI / 2)) * H, 0.18 * H, b, 0.3, 2)
    }
    // The warship in the trough, riding high: ribs first (steps 2–4), plating (steps 3–5).
    const hullL = Lk * 0.84
    const beam = 0.36 * H
    const hullH = 0.5 * H
    const hy = keelY + hullH * 0.62
    const hullRot: Vec3 = [0, -b, -Math.PI / 2]
    const nRibs = 11
    for (let k = 0; k < nRibs; k++) {
      const e = -0.5 + (k + 0.5) / nRibs
      const step = 2 + Math.min(2, Math.floor((3 * k) / nRibs))
      const q: Vec3 = [d[0] * e * hullL, hy - hullH * 0.1, d[2] * e * hullL]
      out.push(part('arc', q, [hullH * 1.1, hullH * 1.1, 30], { rot: [0, Math.PI / 2 - b, Math.PI], arc: Math.PI * 1.1, step, role: 'work' }))
    }
    const plated = (seg: number): number => (stage >= 5 ? 5 : 3 + seg)
    const segs = 3
    for (let s = 0; s < segs; s++) {
      const e0 = -0.5 + s / segs
      const q: Vec3 = [d[0] * (e0 + 0.5 / segs) * hullL, hy, d[2] * (e0 + 0.5 / segs) * hullL]
      const isBow = s === segs - 1
      const taper = isBow ? [0.12, 0.35, 0.6, 0.5][hullI] : s === 0 ? 0.92 : 1
      out.push(part('slab', q, [hullH, hullL / segs, beam], { rot: hullRot, taper, step: plated(s), role: 'signature' }))
    }
    // Dorsal wedge along the hull's spine (step 4), raked forward-to-aft.
    out.push(part('blade', [-d[0] * hullL * 0.08, hy + hullH * 0.5 + 0.07 * H, -d[2] * hullL * 0.08], [hullL * 0.62, 0.14 * H, beam * 0.5], { rot: [0, -b, 0], taper: 0.7, shear: -0.25, step: 4, role: 'signature' }))
    const bow: Vec3 = [d[0] * hullL * 0.5, hy, d[2] * hullL * 0.5]
    if (hullI === 1) out.push(part('slab', [bow[0] + d[0] * 0.2 * H, hy, bow[2] + d[2] * 0.2 * H], [beam * 0.3, 0.4 * H, beam * 0.3], { rot: hullRot, taper: 0.1, step: 5, role: 'signature' }))
    else if (hullI === 2) out.push(part('slab', bow, [hullH * 0.6, 0.14 * H, beam * 2.4], { rot: hullRot, taper: 0.9, step: 5, role: 'signature' }))
    else if (hullI === 3) {
      for (const s of [-1, 1]) out.push(part('slab', [bow[0] + pp[0] * s * beam * 0.3 + d[0] * 0.15 * H, hy, bow[2] + pp[2] * s * beam * 0.3 + d[2] * 0.15 * H], [hullH * 0.6, 0.3 * H, beam * 0.3], { rot: hullRot, taper: 0.2, step: 5, role: 'signature' }))
    }
    // The raked bridge blade on the hull (step 5).
    out.push(part('blade', [-d[0] * hullL * 0.22, hy + hullH * 0.5 + 0.22 * H, -d[2] * hullL * 0.22], [0.26 * H, 0.3 * H, 0.07 * H], { rot: [0, -b, 0], taper: 0.4, shear: -0.4, step: 5, role: 'signature' }))
    return out
  },
}
